<script lang="ts">
  import { buttonVariants } from '$lib/components/ui/button/index.js'
  import { Progress } from '$lib/components/ui/progress/index.js'
  import BookOpen from '@lucide/svelte/icons/book-open'
  import ServerIcon from '@lucide/svelte/icons/server'
  import LibraryIcon from '@lucide/svelte/icons/library'
  import { route } from '@mateothegreat/svelte5-router'
  import PLACEHOLDER from '$assets/icons/book/cover_placeholder.svg'
  import { servers } from '$lib/store'
  import { getLibraries } from '$lib/apis/library/api'
  import NavBreadcrumb from '$lib/components/nuread/NavBreadcrumb.svelte'
  import { serverHash } from '$lib/utils'

  let {
    series,
    embedBreadcrumb = false,
  }: {
    series: any
    embedBreadcrumb?: boolean
  } = $props()

  const taglines = [
    'Pick up where you left off',
    'Your story isn’t finished yet',
    'The page is still warm',
    'One more chapter won’t hurt',
    'Back into the rabbit hole',
    'The plot thickens again',
    'Bookmark called — it misses you',
    'Dive back in',
    'The cliffhanger awaits',
    'Right where the magic paused',
  ] as const

  const tagline = taglines[Math.floor(Math.random() * taglines.length)]

  const mediaId = $derived(series?.mediaId)
  const libraryId = $derived(series?.libraryId)
  const seriesId = $derived(series?.id)
  const Infos = $derived(series?.Infos)
  const Status = $derived(series?.Status)
  const ProgressInfo = $derived(series?.Progress)
  const displayName = $derived(Infos?.title || series?.localTitle || 'Untitled')
  const kind = $derived(series?.kind?.toLowerCase?.() ?? '')
  const server = $derived(mediaId ? $servers[mediaId] : undefined)
  const serverName = $derived(server?.name?.trim() || undefined)
  const serverTag = $derived(mediaId ? serverHash(mediaId) : undefined)
  const cdnUrl = $derived(server?.urls?.[0] ?? '')

  const completed = $derived(ProgressInfo?.Completed ?? 0)
  const total = $derived(ProgressInfo?.Total ?? 0)
  const progressPct = $derived(
    total > 0 ? Math.min(100, Math.round((100 * completed) / total)) : 0
  )
  const progressLabel = $derived(
    total > 0 ? `${completed} of ${total}` : 'In progress'
  )

  const hasRemoteCover = $derived(!!Status?.hasCover)
  let coverFailed = $state(false)

  const coverSrc = $derived.by(() => {
    if (!hasRemoteCover || coverFailed || !seriesId || !cdnUrl) return PLACEHOLDER
    const v = Infos?.refreshedAt ? new Date(Infos.refreshedAt).getTime() : 0
    const bust = v ? `?v=${v}` : ''
    let src = `${cdnUrl}/api/assets/cover/${seriesId}${bust}`
    if (cdnUrl.indexOf('http:') != -1) {
      const qs = [`upstream_addr=${cdnUrl.replace('http://', '')}`, v ? `v=${v}` : '']
        .filter(Boolean)
        .join('&')
      src = `https://proxy.nuread.direct.habysmol.com/api/assets/cover/${seriesId}?${qs}`
    }
    return src
  })

  const href = $derived(
    mediaId && libraryId && seriesId
      ? `/media/${mediaId}/library/${libraryId}/series/${seriesId}`
      : '/'
  )

  let libraryName = $state<string | undefined>()

  $effect(() => {
    coverFailed = false
  })

  $effect(() => {
    if (!server || !libraryId) {
      libraryName = undefined
      return
    }
    let cancelled = false
    getLibraries(server).then(r => {
      if (cancelled) return
      libraryName = (r?.Libraries ?? []).find((l: any) => l.id === libraryId)?.name
    })
    return () => {
      cancelled = true
    }
  })
</script>

{#if series || embedBreadcrumb}
  <section
    class="resume-hero relative isolate mt-6 mb-2 overflow-hidden
      {series
        ? 'min-h-[min(48vh,26rem)] sm:min-h-[min(52vh,28rem)]'
        : 'min-h-0'}"
  >
    {#if embedBreadcrumb}
      <div class="absolute top-4 left-2 z-[10] max-w-[min(100%,42rem)] sm:left-4">
        <NavBreadcrumb />
      </div>
    {/if}

    {#if series}
    <img
      src={coverSrc}
      alt=""
      aria-hidden="true"
      class="resume-hero__bg absolute inset-0 size-full scale-110 object-cover opacity-45 blur-2xl
        dark:opacity-35"
    />
    <div
      class="absolute inset-0 bg-linear-to-r from-latte-base via-latte-base/85 to-latte-base/35
        dark:from-macchiato-base dark:via-macchiato-base/90 dark:to-macchiato-base/40"
    ></div>
    <div
      class="absolute inset-0 bg-linear-to-t from-latte-base via-transparent to-latte-base/50
        dark:from-macchiato-base dark:to-macchiato-base/60"
    ></div>

    <div
      class="relative z-[1] flex h-full min-h-[inherit] flex-col justify-end gap-6 px-4 pb-8 pt-28
        sm:flex-row sm:items-end sm:gap-8 sm:px-10 sm:pb-10"
    >
      <a
        {href}
        use:route
        class="resume-hero__cover group relative shrink-0 self-start sm:self-end"
      >
        <img
          src={coverSrc}
          alt={displayName}
          class="h-48 w-auto rounded-md object-cover shadow-2xl ring-1 ring-latte-surface1/40
            transition-transform duration-500 group-hover:scale-[1.02]
            dark:ring-macchiato-surface1/40 sm:h-56"
          onerror={() => (coverFailed = true)}
        />
      </a>

      <div class="resume-hero__copy min-w-0 flex-1 pb-1">
        <span
          class="text-[10px] font-medium tracking-wide text-latte-green
            dark:text-macchiato-green"
        >
          {tagline}
        </span>
        <h2
          class="mt-1 max-w-xl text-2xl font-bold tracking-tight text-latte-text
            sm:text-3xl dark:text-macchiato-text"
        >
          <a {href} use:route class="hover:opacity-90">{displayName}</a>
        </h2>

        <div
          class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm
            text-latte-text/70 dark:text-macchiato-text/70"
        >
          {#if kind}
            <span class="capitalize">{kind}</span>
          {/if}
          {#if serverName || serverTag}
            <span class="inline-flex items-center gap-1">
              <ServerIcon
                class="size-3.5 text-latte-blue dark:text-macchiato-blue"
                aria-hidden="true"
              />
              <span>
                {serverName ?? '…'}{#if serverTag}<span class="font-normal opacity-55">
                    #{serverTag}</span
                  >{/if}
              </span>
            </span>
          {/if}
          {#if libraryName}
            <span class="inline-flex items-center gap-1">
              <LibraryIcon
                class="size-3.5 text-latte-peach dark:text-macchiato-peach"
                aria-hidden="true"
              />
              {libraryName}
            </span>
          {/if}
        </div>

        <div class="mt-4 max-w-sm">
          <div class="mb-1.5 flex justify-between text-xs text-latte-text/60 dark:text-macchiato-text/60">
            <span>{progressLabel}</span>
            <span>{progressPct}%</span>
          </div>
          <Progress
            value={progressPct}
            max={100}
            class="h-1.5 bg-latte-surface1/50 dark:bg-macchiato-surface1/50
              [&_[data-slot=progress-indicator]]:bg-latte-green
              dark:[&_[data-slot=progress-indicator]]:bg-macchiato-green"
          />
        </div>

        <div class="mt-5">
          <a
            {href}
            use:route
            class="{buttonVariants({ size: 'lg' })} bg-latte-green text-white hover:bg-latte-green/90
              dark:bg-macchiato-green dark:text-macchiato-base dark:hover:bg-macchiato-green/90"
          >
            <BookOpen class="size-4" />
            Resume
          </a>
        </div>
      </div>
    </div>
    {:else}
      <div class="h-14"></div>
    {/if}
  </section>
{/if}

<style>
  .resume-hero__bg {
    animation: resume-bg-in 1.1s ease-out both;
  }
  .resume-hero__cover {
    animation: resume-cover-in 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
  }
  .resume-hero__copy {
    animation: resume-copy-in 0.75s cubic-bezier(0.22, 1, 0.36, 1) 0.08s both;
  }

  @keyframes resume-bg-in {
    from {
      opacity: 0;
      transform: scale(1.2);
    }
    to {
      transform: scale(1.1);
    }
  }
  @keyframes resume-cover-in {
    from {
      opacity: 0;
      transform: translateY(12px) scale(0.96);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  @keyframes resume-copy-in {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
</style>
